import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Receipt, Search, Filter, RefreshCw, AlertTriangle, FileText,
  ShieldCheck, Clock, Ban, ChevronRight, Loader2, ArchiveX, Sparkles,
} from 'lucide-react';

import VATClaimDashboardCards from '../components/VATClaimDashboardCards';
import MoveClaimPeriodModal from '../components/MoveClaimPeriodModal';
import VATClaimAuditTimeline from '../components/VATClaimAuditTimeline';

/**
 * VAT Claim Lifecycle — main workspace.
 *
 * Sections (top → bottom):
 *   1) Five-card dashboard (also acts as tab switcher).
 *   2) Filter row: document_type · period · search · refresh.
 *   3) Document table with bulk selection.
 *   4) Bulk action bar (Move Period · Forfeit · Reclaim).
 *   5) Side panel: per-document audit timeline.
 *
 * All numbers come from /api/vat-claims/* (zero local maths).
 */
const formatPeriod = (p) => {
  if (!p) return '—';
  if (p.includes('Q')) {
    const [y, q] = p.split('-Q');
    return `Q${q} ${y}`;
  }
  return p;
};

const STATUS_BADGE = {
  claimable: { tone: 'indigo', label: 'جاهز' },
  deferred:  { tone: 'amber',  label: 'مُؤَجَّل' },
  claimed:   { tone: 'green',  label: 'تَمَّت المُطالَبة' },
  forfeited: { tone: 'red',    label: 'سُقوط' },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_BADGE[status] || { tone: 'slate', label: status || '—' };
  const cls = {
    indigo: 'bg-indigo-100 text-indigo-800',
    amber:  'bg-amber-100 text-amber-800',
    green:  'bg-green-100 text-green-800',
    red:    'bg-red-100 text-red-800',
    slate:  'bg-slate-100 text-slate-800',
  }[meta.tone];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>
      {meta.label}
    </span>
  );
};

export default function VATClaimManagement({ user }) {
  const [docType, setDocType] = useState('supplier_bill');
  const [activeTab, setActiveTab] = useState('ready');
  const [search, setSearch] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [auditDocId, setAuditDocId] = useState(null);

  const isAdmin = (user?.role === 'admin' || user?.role === 'pilot_admin');

  // ── data loaders ───────────────────────────────────────────────
  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        document_type: docType,
        tab:           activeTab,
        limit:         200,
      };
      if (periodFilter) params.period = periodFilter;
      if (search) params.q = search;
      const { data } = await axiosInstance.get('/vat-claims', { params });
      setItems(data.items || []);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'تَعَذَّر جَلب القائِمة');
    } finally {
      setLoading(false);
    }
  }, [docType, activeTab, periodFilter, search]);

  const loadDashboard = useCallback(async () => {
    try {
      setDashLoading(true);
      const { data } = await axiosInstance.get('/vat-claims/dashboard');
      setDashboard(data);
    } catch {
      // Dashboard is best-effort.
    } finally {
      setDashLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Auto-populate the period filter with the CURRENT filing period
  // once the dashboard reports it (only on first load, so the user
  // stays in control after they touch the filter).
  useEffect(() => {
    if (dashboard?.current_period && periodFilter === '') {
      setPeriodFilter(dashboard.current_period);
    }
  }, [dashboard, periodFilter]);
  useEffect(() => { loadList(); }, [loadList]);

  // ── bulk actions ───────────────────────────────────────────────
  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const selectedTotal = useMemo(
    () => items.filter((i) => selectedIds.has(i.id)).reduce(
      (s, i) => s + (i.vat_amount || 0), 0,
    ),
    [items, selectedIds],
  );

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((i) => i.id)));
  };

  const onMoveDone = () => {
    setSelectedIds(new Set());
    loadList();
    loadDashboard();
  };

  const bulkForfeit = async () => {
    if (selectedArray.length === 0) return;
    if (!window.confirm(
      `تَأكيد سَقوط ${selectedArray.length} فاتورة بِالتَّقادُم؟\nلا يُمكِن استِرداد ضَريبة المَدخَلات بَعد ذلك.`
    )) return;
    try {
      const reason = window.prompt('السَّبَب (اختياري):', 'إجراء يَدَوي — لَن يَتِم المُطالَبة');
      const { data } = await axiosInstance.post('/vat-claims/forfeit', {
        document_ids: selectedArray,
        document_type: docType,
        reason: reason || null,
      });
      const ok = data.forfeited?.length || 0;
      if (ok) toast.success(`تَم تَسجيل سُقوط ${ok} فاتورة`);
      onMoveDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فَشِل الإجراء');
    }
  };

  const bulkReclaim = async () => {
    if (selectedArray.length === 0) return;
    if (!window.confirm(
      `إِعادة ${selectedArray.length} فاتورة إلى حالة "جاهز لِلمُطالَبة"؟`
    )) return;
    try {
      const { data } = await axiosInstance.post('/vat-claims/reclaim', {
        document_ids: selectedArray,
        document_type: docType,
        reason: 'Manual reclaim',
      });
      const ok = data.reclaimed?.length || 0;
      if (ok) toast.success(`تَمَّت إِعادَة ${ok} فاتورة`);
      onMoveDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فَشِل الإجراء');
    }
  };

  const adminBackfill = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await axiosInstance.post('/vat-claims/backfill');
      toast.success(`Backfill: ${data.supplier_bills_updated} فاتورة + ${data.invoices_updated} فاتورة عَميل`);
      loadList(); loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فَشِل الـ Backfill');
    }
  };

  const adminAutoExpire = async () => {
    if (!isAdmin) return;
    if (!window.confirm('فَحص كُل الفَواتير التي تَجاوَزَت 5 سَنوات وتَحويلها تِلقائياً إلى Forfeited؟')) return;
    try {
      const { data } = await axiosInstance.post('/vat-claims/auto-expire');
      const c = data.forfeited?.length || 0;
      if (c) toast.success(`سَقَطَت ${c} فاتورة بِالتَّقادُم تِلقائياً`);
      else   toast.success('لا تُوجَد فَواتير مُنتَهية الصَّلاحية');
      loadList(); loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فَشِل الإجراء');
    }
  };

  // ── render ─────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-5" data-testid="vat-claim-management-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Receipt className="w-7 h-7 text-indigo-600" />
            إِدارة المُطالَبات الضَّريبية
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            دَورَة حَياة كامِلَة لِضَريبة القيمة المُضافة — وَفق نِظام الزكاة والضَّريبة السُّعودي.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={adminBackfill}
              data-testid="vat-admin-backfill"
              className="px-3 py-1.5 rounded-lg text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 inline-flex items-center gap-1"
              title="استِكمال حُقول المُطالَبة عَلى السِجِلَّات القَديمة"
            >
              <Sparkles className="w-3.5 h-3.5" /> Backfill
            </button>
            <button
              onClick={adminAutoExpire}
              data-testid="vat-admin-auto-expire"
              className="px-3 py-1.5 rounded-lg text-xs bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 inline-flex items-center gap-1"
              title="فَحص أَوتوماتيكي لِلفَواتير المُنتَهية بِالتَّقادُم"
            >
              <ArchiveX className="w-3.5 h-3.5" /> Auto-Expire
            </button>
          </div>
        )}
      </div>

      <VATClaimDashboardCards
        data={dashboard}
        loading={dashLoading}
        activeTab={activeTab}
        onTabChange={(tab) => {
          // Wave STA-2 · owner directive 2026-07-05: clicking a card
          // must land the user on a docType that *actually* has rows
          // in that status. Previously we only flipped ``activeTab``,
          // leaving ``docType`` pinned — so tenants whose deferred
          // items lived under a different collection saw "no rows".
          setActiveTab(tab);
          const bd = dashboard?.cards?.[
            tab === 'ready'      ? 'ready_to_claim_current' :
            tab === 'deferred'   ? 'deferred_total'         :
            tab === 'claimed'    ? 'claimed_current'        :
            tab === 'near_expiry'? 'near_expiry'            :
            tab === 'forfeited'  ? 'forfeited_ytd'          : ''
          ]?.by_doc_type;
          if (!bd) return;
          // Preserve the current docType if it still has hits; else
          // switch to the type carrying the biggest count.
          const currentCount = bd[docType]?.count || 0;
          if (currentCount > 0) return;
          const best = Object.entries(bd)
            .sort((a, b) => (b[1]?.count || 0) - (a[1]?.count || 0))[0];
          if (best && (best[1]?.count || 0) > 0) setDocType(best[0]);
        }}
      />

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-2 flex-wrap">
        {(() => {
          // Compute counts for the currently-selected status tab so
          // each doc-type button carries a small pill badge — the
          // user sees at a glance where the rows actually live and
          // never has to click a "0" tab.
          const cardKey =
            activeTab === 'ready'      ? 'ready_to_claim_current' :
            activeTab === 'deferred'   ? 'deferred_total'         :
            activeTab === 'claimed'    ? 'claimed_current'        :
            activeTab === 'near_expiry'? 'near_expiry'            :
            activeTab === 'forfeited'  ? 'forfeited_ytd'          : '';
          const bd = dashboard?.cards?.[cardKey]?.by_doc_type || {};
          const badge = (k) => {
            const n = bd[k]?.count;
            if (!n) return null;
            return (
              <span
                className="ms-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] px-1 bg-indigo-600 text-white font-semibold"
                data-testid={`vat-doctype-badge-${k}`}
              >
                {n}
              </span>
            );
          };
          return (
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              <button
                onClick={() => setDocType('supplier_bill')}
                data-testid="vat-doctype-supplier"
                className={`px-3 py-1.5 text-xs rounded-md font-semibold inline-flex items-center ${
                  docType === 'supplier_bill'
                    ? 'bg-white shadow text-indigo-700'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                فَواتير مَوَرِّدين (مَدخَلات) {badge('supplier_bill')}
              </button>
              <button
                onClick={() => setDocType('customer_invoice')}
                data-testid="vat-doctype-customer"
                className={`px-3 py-1.5 text-xs rounded-md font-semibold inline-flex items-center ${
                  docType === 'customer_invoice'
                    ? 'bg-white shadow text-indigo-700'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                فَواتير عُمَلاء (مَخرَجات) {badge('customer_invoice')}
              </button>
              <button
                onClick={() => setDocType('expense_report')}
                data-testid="vat-doctype-expenses"
                className={`px-3 py-1.5 text-xs rounded-md font-semibold inline-flex items-center ${
                  docType === 'expense_report'
                    ? 'bg-white shadow text-indigo-700'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                مَصروفات (تقارير) {badge('expense_report')}
              </button>
              <button
                onClick={() => setDocType('bank_adjustment')}
                data-testid="vat-doctype-bank"
                className={`px-3 py-1.5 text-xs rounded-md font-semibold inline-flex items-center ${
                  docType === 'bank_adjustment'
                    ? 'bg-white shadow text-indigo-700'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                تَسويات بَنكية {badge('bank_adjustment')}
              </button>
            </div>
          );
        })()}

        <div className="flex items-center gap-1 bg-slate-50 rounded-lg px-2 py-1.5 border border-slate-200 flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="vat-search-input"
            placeholder="بَحث بِرَقم الفاتورة أو اسم المُورِّد..."
            className="bg-transparent border-0 outline-none text-sm flex-1"
          />
        </div>

        <div className="flex items-center gap-1 bg-slate-50 rounded-lg px-2 py-1.5 border border-slate-200">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            data-testid="vat-period-filter"
            placeholder="مَثَلاً 2026-Q2"
            className="bg-transparent border-0 outline-none text-sm w-32"
          />
        </div>

        <button
          onClick={loadList}
          data-testid="vat-refresh-btn"
          className="px-3 py-1.5 rounded-lg text-xs bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          تَحديث
        </button>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl p-3 flex items-center justify-between shadow-md"
          data-testid="vat-bulk-action-bar"
        >
          <div className="text-sm">
            <span className="font-bold">{selectedIds.size}</span> فاتورة مُحَدَّدَة ·{' '}
            مَجموع الـ VAT:{' '}
            <span className="font-mono font-bold">
              {selectedTotal.toLocaleString(undefined, {
                minimumFractionDigits: 2, maximumFractionDigits: 2,
              })} SAR
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMoveModal(true)}
              data-testid="vat-bulk-move"
              className="px-3 py-1.5 rounded-lg text-xs bg-white text-indigo-700 hover:bg-slate-100 font-semibold inline-flex items-center gap-1"
            >
              <Clock className="w-3.5 h-3.5" /> نَقل لِفَترة أُخرى
            </button>
            <button
              onClick={bulkReclaim}
              data-testid="vat-bulk-reclaim"
              className="px-3 py-1.5 rounded-lg text-xs bg-white/20 hover:bg-white/30 text-white font-semibold inline-flex items-center gap-1"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> إِعادَة لِلجاهز
            </button>
            {isAdmin && (
              <button
                onClick={bulkForfeit}
                data-testid="vat-bulk-forfeit"
                className="px-3 py-1.5 rounded-lg text-xs bg-red-600 hover:bg-red-700 text-white font-semibold inline-flex items-center gap-1"
              >
                <Ban className="w-3.5 h-3.5" /> سُقوط بِالتَّقادُم
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Table */}
        <div className="xl:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vat-claims-table">
              <thead className="bg-slate-50 text-slate-600 text-xs">
                <tr>
                  <th className="p-2 text-start">
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selectedIds.size === items.length}
                      onChange={selectAll}
                      data-testid="vat-select-all"
                    />
                  </th>
                  <th className="p-2 text-start">رَقم</th>
                  <th className="p-2 text-start">
                    {docType === 'supplier_bill' ? 'المُورِّد'
                      : docType === 'customer_invoice' ? 'العَميل'
                      : docType === 'expense_report' ? 'المُوظَّف / الجِهة'
                      : docType === 'bank_adjustment' ? 'البَنك / الطَّرَف'
                      : 'الطَّرَف'}
                  </th>
                  <th className="p-2 text-start">تاريخ المُستَنَد</th>
                  <th className="p-2 text-end">القيمة (شامِل VAT)</th>
                  <th className="p-2 text-end">VAT</th>
                  <th className="p-2 text-center">الحالة</th>
                  <th className="p-2 text-center">فَترة المُطالَبة</th>
                  <th className="p-2 text-center">انتِهاء الصَّلاحية</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="p-8 text-center text-slate-500">
                      <Loader2 className="w-5 h-5 animate-spin inline me-2" />
                      جارٍ التَّحميل...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="p-8 text-center text-slate-500">
                      <FileText className="w-8 h-8 inline mb-2 opacity-30" />
                      <br />لا تُوجَد فَواتير في هَذِه الحالة.
                    </td>
                  </tr>
                ) : (
                  items.map((it) => {
                    const isNearExpiry =
                      it.vat_claim_expires_at &&
                      new Date(it.vat_claim_expires_at) <
                        new Date(new Date().setMonth(new Date().getMonth() + 6));
                    return (
                      <tr
                        key={it.id}
                        className={`border-t border-slate-100 hover:bg-slate-50 ${
                          selectedIds.has(it.id) ? 'bg-indigo-50/40' : ''
                        }`}
                        data-testid={`vat-claim-row-${it.id}`}
                      >
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(it.id)}
                            onChange={() => toggleSelect(it.id)}
                            data-testid={`vat-select-${it.id}`}
                          />
                        </td>
                        <td className="p-2 font-mono text-xs text-slate-900">{it.document_no}</td>
                        <td className="p-2 text-xs text-slate-700">
                          {it.supplier_or_customer_name || '—'}
                        </td>
                        <td className="p-2 text-xs text-slate-500">{it.document_date || '—'}</td>
                        <td className="p-2 text-end font-mono text-xs">
                          {(it.total ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="p-2 text-end font-mono text-xs font-semibold text-indigo-700">
                          {(it.vat_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="p-2 text-center">
                          <StatusBadge status={it.vat_claim_status} />
                        </td>
                        <td className="p-2 text-center text-xs font-semibold text-slate-700">
                          {formatPeriod(it.vat_claim_period)}
                          {it.vat_claim_period !== it.vat_claim_default_period && it.vat_claim_default_period && (
                            <div className="text-[9px] text-amber-700 mt-0.5">
                              مَنقول مِن {formatPeriod(it.vat_claim_default_period)}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-center text-xs">
                          <span className={isNearExpiry ? 'text-red-700 font-semibold' : 'text-slate-500'}>
                            {it.vat_claim_expires_at || '—'}
                          </span>
                          {isNearExpiry && (
                            <AlertTriangle className="w-3 h-3 inline ms-1 text-red-600" />
                          )}
                        </td>
                        <td className="p-2 text-center">
                          <button
                            onClick={() => setAuditDocId(it.id)}
                            data-testid={`vat-claim-audit-${it.id}`}
                            className="text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-0.5"
                            title="عَرض سِجِل التَّدقيق"
                          >
                            سِجِل <ChevronRight className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Audit timeline side panel */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 min-h-[300px]">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-indigo-600" />
            سِجِل التَّدقيق
          </h3>
          {!auditDocId ? (
            <div className="text-xs text-slate-500 italic">
              اختَر فاتورة لِعَرض كامِل سِجِل المُطالَبة الضَّريبية الخاص بِها.
            </div>
          ) : (
            <VATClaimAuditTimeline documentId={auditDocId} documentType={docType} />
          )}
        </div>
      </div>

      {showMoveModal && (
        <MoveClaimPeriodModal
          documentIds={selectedArray}
          documentType={docType}
          currentPeriod={
            items.find((i) => i.id === selectedArray[0])?.vat_claim_period
          }
          onClose={() => setShowMoveModal(false)}
          onDone={onMoveDone}
        />
      )}
    </div>
  );
}
